import * as XLSX from 'xlsx';

export const PO_UPLOAD_ALLOWED_EXTENSIONS = ['.csv', '.xlsx', '.xls'];

/** Legacy `stockid` is normalized to stockid_internal when reading mappings. */
export const PO_AVAILABLE_FIELD_TYPES = [
	'stockid_internal',
	'mfg_part',
	'brand',
	'qty_purchase',
	'price',
	'description',
	'vendor',
	'ignore',
] as const;

export type PoUploadFieldType = (typeof PO_AVAILABLE_FIELD_TYPES)[number];

export const PO_FIELD_SYNONYMS: Record<string, string[]> = {
	stockid_internal: [
		'stockid',
		'sku',
		'itemnumber',
		'itemnum',
		'itemcode',
		'pll',
		'pll#',
		'internalsku',
		'productcode',
		'inventoryid',
	],
	mfg_part: [
		'mfgpartno',
		'mfgpart',
		'mfg#',
		'supplierspartno',
		'supplierpart',
		'supplierpartno',
		'vendorpart',
		'vendorpartno',
		'vpn',
		'partnumber',
		'partnum',
		'productnumber',
		'manufacturerpart',
		'manupart',
		'upc',
		'ean',
	],
	brand: ['brand', 'mfgname', 'manufacturername', 'line', 'make', 'vendorname', 'suppliername'],
	qty_purchase: ['qty', 'quantity', 'qtypurchase', 'orderqty', 'qtyordered', 'purchaseqty', 'orderquantity'],
	price: ['price', 'unitprice', 'sellprice', 'listprice', 'netprice', 'unitcost', 'cost'],
	description: ['description', 'desc', 'longdesc', 'title', 'name'],
	vendor: ['vendor', 'supplier', 'supp', 'vendorno', 'supplierid', 'vendorsku'],
};

/** Maps saved / legacy dropdown values to canonical targets. */
export function normalizePoMappingTarget(field: string): string {
	if (!field || field === 'ignore') {
		return field;
	}
	if (field === 'stockid') {
		return 'stockid_internal';
	}
	return field;
}

export function collectPoMappedTargets(columnMappings: { [header: string]: string }): Set<string> {
	const s = new Set<string>();
	Object.keys(columnMappings).forEach((header) => {
		const t = normalizePoMappingTarget(columnMappings[header]);
		if (t && t !== 'ignore') {
			s.add(t);
		}
	});
	return s;
}

export type PoItemIdentification =
	| { ok: true; mode: 'internal' | 'mfg'; brandMapped: boolean }
	| { ok: false; error: string };

/**
 * Exactly one item key strategy: internal stock ID column(s) XOR manufacturer part (optional brand column).
 */
export function poUploadResolveItemIdentification(columnMappings: { [header: string]: string }): PoItemIdentification {
	const targets = collectPoMappedTargets(columnMappings);
	const hasInternal = targets.has('stockid_internal');
	const hasMfg = targets.has('mfg_part');
	if (hasInternal && hasMfg) {
		return {
			ok: false,
			error: 'Map either Internal stock ID or Manufacturer part # — not both.',
		};
	}
	if (!hasInternal && !hasMfg) {
		return {
			ok: false,
			error: 'Map Internal stock ID, or Manufacturer part # (add Brand if the sheet splits them).',
		};
	}
	if (hasInternal) {
		return { ok: true, mode: 'internal', brandMapped: false };
	}
	return { ok: true, mode: 'mfg', brandMapped: targets.has('brand') };
}

export function getPoMatchOnMfgFromMappings(columnMappings: { [header: string]: string }): boolean {
	const id = poUploadResolveItemIdentification(columnMappings);
	return id.ok && id.mode === 'mfg';
}

export function poItemIdentificationHint(columnMappings: { [header: string]: string }): string {
	const id = poUploadResolveItemIdentification(columnMappings);
	if (!id.ok) {
		return '';
	}
	if (id.mode === 'internal') {
		return 'Using mapped column(s) as your internal stock ID (PLL/SKU). No manufacturer lookup.';
	}
	if (id.brandMapped) {
		return 'Using Brand + Manufacturer part # per row for supplier / MFG part lookup.';
	}
	return 'Using mapped column as manufacturer / supplier part # for lookup.';
}

export function poUploadFieldTypeLabel(type: string, tiresPackage?: boolean): string {
	const pll = tiresPackage ? 'PLL #' : 'internal stock ID';
	const labels: Record<string, string> = {
		stockid_internal: `Internal stock ID (${pll})`,
		mfg_part: 'Manufacturer / supplier part #',
		brand: 'Brand (use with MFG part #)',
		qty_purchase: 'Order quantity',
		price: 'Unit price (optional)',
		description: 'Description (optional)',
		vendor: 'Vendor code in file (optional)',
		ignore: '— Ignore —',
	};
	return labels[type] || type;
}

export function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = (e) => resolve((e.target as FileReader).result as ArrayBuffer);
		reader.onerror = () => reject(reader.error);
		reader.readAsArrayBuffer(file);
	});
}

export function readFileAsTextUtf8(file: File): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(String(reader.result ?? ''));
		reader.onerror = () => reject(reader.error);
		reader.readAsText(file, 'UTF-8');
	});
}

export function normalizeHeaderKey(k: string): string {
	return String(k).replace(/^\uFEFF/, '').trim();
}

export function normalizeDataRow(row: Record<string, unknown>): Record<string, string> {
	const o: Record<string, string> = {};
	for (const k of Object.keys(row)) {
		if (k === '__rowNum__') {
			continue;
		}
		const nk = normalizeHeaderKey(k);
		if (nk === '') {
			continue;
		}
		const v = row[k];
		o[nk] = v === null || v === undefined ? '' : String(v).trim();
	}
	return o;
}

function normalizeForScore(s: string): string {
	return String(s || '')
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '');
}

function scorePoFieldHeader(field: string, header: string): number {
	const hn = normalizeForScore(header);
	if (!hn) {
		return 0;
	}
	const fn = normalizeForScore(field);
	if (hn === fn) {
		return 100;
	}
	let best = 0;
	const syns = PO_FIELD_SYNONYMS[field];
	if (syns) {
		for (const syn of syns) {
			const sn = normalizeForScore(syn);
			if (!sn) {
				continue;
			}
			if (hn === sn) {
				best = Math.max(best, 92);
			} else if (hn.includes(sn) && sn.length >= 4) {
				best = Math.max(best, 72);
			} else if (hn.includes(sn) && sn.length >= 3) {
				best = Math.max(best, 52);
			} else if (sn.includes(hn) && hn.length >= 4) {
				best = Math.max(best, 58);
			}
		}
	}
	return best;
}

export function applyGreedyPoAutoMap(
	uploadedHeaders: string[],
	columnMappings: { [header: string]: string },
	availableFieldTypes: readonly string[]
): void {
	const targets = availableFieldTypes.filter((t) => t !== 'ignore');
	const fieldIndex = (f: string) => targets.indexOf(f);
	const pairs: { field: string; header: string; score: number }[] = [];
	for (const header of uploadedHeaders) {
		for (const field of targets) {
			const sc = scorePoFieldHeader(field, header);
			if (sc >= 40) {
				pairs.push({ field, header, score: sc });
			}
		}
	}
	pairs.sort((a, b) => {
		if (b.score !== a.score) {
			return b.score - a.score;
		}
		return fieldIndex(a.field) - fieldIndex(b.field);
	});
	const usedHeaders = new Set<string>();
	const usedFields = new Set<string>();
	uploadedHeaders.forEach((h) => {
		columnMappings[h] = 'ignore';
	});
	for (const p of pairs) {
		if (usedHeaders.has(p.header) || usedFields.has(p.field)) {
			continue;
		}
		columnMappings[p.header] = p.field;
		usedHeaders.add(p.header);
		usedFields.add(p.field);
	}
}

export function parseNumericCell(v: unknown): number {
	if (v === null || v === undefined || v === '') {
		return NaN;
	}
	if (typeof v === 'number' && !isNaN(v)) {
		return v;
	}
	let s = String(v).trim();
	s = s.replace(/[$£€]/g, '').replace(/\s/g, '');
	if (/%\s*$/.test(s)) {
		s = s.replace(/%\s*$/, '');
	}
	s = s.replace(/,/g, '');
	const n = parseFloat(s);
	return isNaN(n) ? NaN : n;
}

export interface PoUploadLineInput {
	stockid: string;
	quantity: number;
	vendor: string;
	price: string | number;
	description: string;
	discount: number;
}

export interface BuildPoUploadLineInputsResult {
	items: PoUploadLineInput[];
	skippedRows: number;
}

function rowMappedValues(
	row: Record<string, string>,
	columnMappings: { [header: string]: string }
): Record<string, string> {
	const mapped: Record<string, string> = {};
	Object.keys(row).forEach((header) => {
		const field = normalizePoMappingTarget(columnMappings[header]);
		if (field && field !== 'ignore') {
			mapped[field] = row[header];
		}
	});
	return mapped;
}

/**
 * Builds PO lines. For MFG mode with a Brand column mapped, each row must have both brand and MFG part.
 */
export function buildPoUploadLineInputs(
	uploadedRawData: Record<string, string>[],
	columnMappings: { [header: string]: string }
): BuildPoUploadLineInputsResult {
	const id = poUploadResolveItemIdentification(columnMappings);
	const items: PoUploadLineInput[] = [];
	let skippedRows = 0;

	if (!id.ok) {
		return { items, skippedRows: 0 };
	}

	uploadedRawData.forEach((row) => {
		const mapped = rowMappedValues(row, columnMappings);
		const qty = parseNumericCell(mapped['qty_purchase']);
		if (isNaN(qty) || qty === 0) {
			return;
		}

		let sid = '';
		if (id.mode === 'internal') {
			sid = (mapped['stockid_internal'] || '').toString().trim();
		} else {
			const part = (mapped['mfg_part'] || '').toString().trim();
			const brand = (mapped['brand'] || '').toString().trim();
			if (!part) {
				return;
			}
			if (id.brandMapped) {
				if (!brand) {
					skippedRows++;
					return;
				}
				sid = `${brand} ${part}`.replace(/\s+/g, ' ').trim();
			} else {
				sid = part;
			}
		}

		if (!sid) {
			return;
		}

		const pRaw = mapped['price'];
		let price: string | number = '';
		if (pRaw !== undefined && String(pRaw).trim() !== '') {
			const n = parseNumericCell(pRaw);
			price = isNaN(n) ? '' : n;
		}
		items.push({
			stockid: sid,
			quantity: qty,
			vendor: (mapped['vendor'] || '').toString().trim(),
			price,
			description: (mapped['description'] || '').toString().trim(),
			discount: 0,
		});
	});

	return { items, skippedRows };
}

export function poUploadHasRequiredMappings(columnMappings: { [header: string]: string }): boolean {
	const id = poUploadResolveItemIdentification(columnMappings);
	if (!id.ok) {
		return false;
	}
	return collectPoMappedTargets(columnMappings).has('qty_purchase');
}

export async function parsePoUploadSpreadsheet(params: {
	file: File;
	workbook: XLSX.WorkBook | null;
	selectedSheetName: string;
	headerRowNumber: number;
}): Promise<{ rows: Record<string, string>[] } | { error: string }> {
	const { file, workbook, selectedSheetName, headerRowNumber } = params;
	const lower = file.name.toLowerCase();
	const headerRow0 = Math.max(0, (Number(headerRowNumber) || 1) - 1);

	try {
		let rows: Record<string, string>[] = [];

		if (lower.endsWith('.xlsx') || lower.endsWith('.xls')) {
			if (!workbook) {
				return { error: 'reload_excel' };
			}
			const ws = workbook.Sheets[selectedSheetName];
			if (!ws) {
				return { error: 'sheet_not_found' };
			}
			const raw = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, {
				range: headerRow0,
				defval: '',
				raw: false,
			});
			rows = raw.map((row) => normalizeDataRow(row));
		} else if (lower.endsWith('.csv')) {
			const text = await readFileAsTextUtf8(file);
			const normalized = text.replace(/^\uFEFF/, '');
			const wb = XLSX.read(normalized, { type: 'string', cellDates: true, raw: false });
			const sn = wb.SheetNames[0];
			if (!sn) {
				return { error: 'csv_empty' };
			}
			const ws = wb.Sheets[sn];
			const raw = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, {
				range: headerRow0,
				defval: '',
				raw: false,
			});
			rows = raw.map((row) => normalizeDataRow(row));
		} else {
			return { error: 'bad_extension' };
		}

		return { rows };
	} catch {
		return { error: 'parse_failed' };
	}
}
